import { AnalyticsService, AuthService, HeaderComponent, OptometristMenuOptions, SidebarContentComponent, ThemeService } from '@ng-shared';
import { MatSidenavModule } from '@angular/material/sidenav';
import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, PLATFORM_ID, Renderer2, inject } from '@angular/core';
import { BookingRequestsTagService } from './pages/bookings/services/booking-requests-tag.service';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { VendorService } from './services/vendorService';
import { isPlatformBrowser } from '@angular/common';
import { NavigationOption, Vendor } from 'common';

@Component({
    standalone: true,
    imports: [RouterModule, MatSidenavModule, HeaderComponent, SidebarContentComponent],
    selector: 'oz-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {

    protected navigationOptions: NavigationOption[] = OptometristMenuOptions;

    protected showHeader: boolean = true;

    protected vendorIsLoaded: boolean = false;

    protected readonly applicationTitle: string = 'Optometrist Zone';

    private readonly destroy$: Subject<void> = new Subject<void>();

    private readonly _analyticsService: AnalyticsService = inject(AnalyticsService);

    private readonly _authService: AuthService = inject(AuthService);

    private readonly _vendorService: VendorService = inject(VendorService);

    private readonly _themeService: ThemeService = inject(ThemeService);

    private readonly _bookingRequestTagService: BookingRequestsTagService = inject(BookingRequestsTagService);

    // eslint-disable-next-line @typescript-eslint/ban-types
    private readonly _platformId: Object = inject(PLATFORM_ID);

    private readonly _renderer: Renderer2 = inject(Renderer2);

    private readonly _router: Router = inject(Router);

    public ngOnInit(): void {
        this._themeService.onResize();

        this.monitorActiveRoute();

        this.monitorVendorLoadState();

        this.subscribeToBookingRequests();

        this._analyticsService.initialize();
    }

    public ngAfterViewInit(): void {
        if (isPlatformBrowser(this._platformId)) {
            const loader: HTMLElement = this._renderer.selectRootElement('#loader');

            if (loader.style.display != "none") {
                loader.style.display = "none";
            }
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private monitorActiveRoute() {
        this._router
            .events
            .subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    this.setActiveNav(this._router.url);
                }
            });
    }

    private monitorVendorLoadState(): void {
        this._vendorService.currentVendor$
            .pipe(takeUntil(this.destroy$))
            .subscribe((vendor: Vendor | undefined) => {
                this.vendorIsLoaded = !!vendor;

                if (vendor) {
                    this._analyticsService.setVendorContext(vendor);
                } else {
                    this._analyticsService.clearVendorContext();
                }
            });
    }

    private subscribeToBookingRequests() {
        this._authService.isAuthenticated$
            .pipe(
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe((isAuthenticated: boolean) => {
                if (isAuthenticated) {
                    this._bookingRequestTagService.startPolling();
                }
                else {
                    this._bookingRequestTagService.pausePolling();
                }
            })

        this._bookingRequestTagService.requestsCount$
            .pipe(takeUntil(this.destroy$))
            .subscribe(count => {
                const bookingsNav: NavigationOption | undefined = this.navigationOptions.find(nav => nav.path === '/bookings');

                if (bookingsNav) {
                    bookingsNav.badgeCount = count;
                }
            });

        this._bookingRequestTagService.hasNewRequests$
            .pipe(takeUntil(this.destroy$))
            .subscribe(hasNew => {
                const bookingsNav: NavigationOption | undefined = this.navigationOptions.find(nav => nav.path === '/bookings');

                if (bookingsNav) {
                    bookingsNav.hasNotification = hasNew;
                }
            });
    }

    private setActiveNav(url: string) {
        this.navigationOptions
            .forEach((nav: NavigationOption) => {
                nav.isCurrentNav = url.includes(nav.path);

                if (nav.children) {
                    nav.children.forEach((child: NavigationOption) => {
                        child.isCurrentNav = url.includes(child.path);
                    });
                }
            });
    }

    /**
     * Event listener for window resize.
     * This is responsible for detecting the screen size and adjusting the layout accordingly, especially for mobile devices.
     */
    @HostListener('window:resize', ['$event'])
    protected onResize(event: UIEvent) {
        this._themeService.onResize((event.target as Window).innerWidth);
    }

}
